"use client";
import {
  Box,
  Divider,
  Flex,
  Heading,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Fragment } from "react";
import { SectionTemplate } from "../section-template";
import { Button, ButtonActions } from "@ui/components";
import { HighlightsSectionProps } from "components/industries/highlights-section";
import { ButtonProp } from "lib/gcms";
import { Gcms_ButtonTheme } from "lib/cms-typings";

export interface StatsBlockProps extends HighlightsSectionProps {
  title: string;
  button: ButtonProp;
  titleColor: string;
  highlightColor?: string;
}

export const StatsBlock = ({
  title,
  highlight,
  color,
  bg,
  button,
  titleColor,
  highlightColor,
}: StatsBlockProps) => {
  const dividerOrientation = useBreakpointValue<"horizontal" | "vertical">({
    base: "horizontal",
    md: "vertical",
  });

  return (
    <SectionTemplate {...{ bg, color }}>
      <Heading
        as={"h2"}
        fontSize={{
          base: 26,
          md: 34,
          lg: 38,
          xl: 45,
        }}
        fontWeight="bold"
        lineHeight={1.2}
        mb={{
          base: 6,
          md: 8,
          lg: 10,
        }}
        textAlign={"center"}
        color={titleColor}
      >
        {title}
      </Heading>
      <Flex
        justifyContent="space-around"
        flexDir={{ base: "column", lg: "row" }}
        alignItems={{ base: "center", lg: "flex-start" }}
      >
        {highlight?.map((content, idx) => (
          <Fragment key={idx}>
            {0 < idx && (
              <Divider
                orientation={dividerOrientation}
                w={{ base: "60px", lg: "0px" }}
                h={{ base: "0px", lg: "60px" }}
                borderColor={titleColor}
                alignSelf="center"
                borderLeftWidth={2}
                borderBottomWidth={1}
                opacity={0.25}
                my={{
                  base: 5,
                  lg: 0,
                }}
              />
            )}
            <Box
              textAlign="center"
              maxW="370px"
              py={{ base: "10px", lg: undefined }}
              px={{ base: undefined, lg: "10px" }}
              pt={{
                base: 3,
                md: 5,
              }}
            >
              <Box
                color={highlightColor ?? titleColor}
                fontWeight="bold"
                fontSize={{
                  base: "2xl",
                  lg: "3xl",
                  xl: "4xl",
                }}
                fontFamily="numerical"
              >
                {content.highlight}
              </Box>
              <Box
                fontSize={{
                  base: "14px",
                  md: "16px",
                  lg: "17px",
                  xl: "18px",
                }}
                pt={2}
              >
                {content.description}
              </Box>
            </Box>
          </Fragment>
        ))}
      </Flex>
      <Flex justify={"center"}>
        <Button
          __type={button.buttonAction as ButtonActions}
          href={button.href as string}
          colorScheme={button?.theme as Gcms_ButtonTheme}
          variant={button.variant}
          label={button.label}
          mt={{
            base: 6,
            md: 8,
            lg: 10,
          }}
          py={{
            base: "6",
            lg: "8",
          }}
          px="5"
        />
      </Flex>
    </SectionTemplate>
  );
};
